import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Users } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'
import { getJobCrewAssignmentContext } from '@/lib/crew-assignment'
import crewsApi from '@/services/crews'
import type { Assignment } from '@/types'

type JobAssignCrewControlsProps = {
  jobId: string | number
  assignments: Assignment[]
  serviceInstanceId?: string | null
  disabled?: boolean
  variant?: 'panel' | 'compact'
  onAssigned?: () => void
}

export function JobAssignCrewControls({
  jobId,
  assignments,
  serviceInstanceId,
  disabled = false,
  variant = 'panel',
  onAssigned,
}: JobAssignCrewControlsProps) {
  const queryClient = useQueryClient()
  const [selectedCrewId, setSelectedCrewId] = useState('')

  const crewContext = useMemo(() => getJobCrewAssignmentContext(assignments), [assignments])

  const readyCrewsQuery = useQuery({
    queryKey: ['ready-crews', crewContext?.crewTemplateId, serviceInstanceId],
    queryFn: () => crewsApi.listReadyCrews({
      crew_template_id: crewContext!.crewTemplateId,
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: Boolean(crewContext?.crewTemplateId && serviceInstanceId),
    staleTime: 30_000,
  })

  const assignMutation = useMutation({
    mutationFn: () => {
      if (!selectedCrewId) {
        throw new Error('Select a staffed team')
      }
      return crewsApi.assignToJob({ job_id: jobId, crew_id: selectedCrewId })
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['job-assignments', String(jobId)] })
      await queryClient.invalidateQueries({ queryKey: ['assignments'] })
      await queryClient.invalidateQueries({ queryKey: ['unassigned-assignments'] })
      await queryClient.invalidateQueries({ queryKey: ['unassigned-assignments-count'] })
      await queryClient.invalidateQueries({ queryKey: ['job', String(jobId)] })
      setSelectedCrewId('')
      toast.success(`Assigned ${result.assigned_count} team member${result.assigned_count === 1 ? '' : 's'} to this job`)
      onAssigned?.()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  if (!crewContext) {
    return null
  }

  const isCompact = variant === 'compact'

  return (
    <div
      className={cn(
        'rounded-lg border border-amber-200 bg-amber-50/60',
        isCompact ? 'px-3 py-2' : 'mb-4 rounded-xl p-4',
      )}
    >
      <div className={cn('flex gap-3', isCompact ? 'flex-col sm:flex-row sm:items-center sm:justify-between' : 'flex-col md:flex-row md:items-end md:justify-between')}>
        <div className="space-y-1">
          <p className={cn('font-medium text-foreground', isCompact ? 'text-xs' : 'text-sm')}>Assign staffed team</p>
          <p className="text-xs text-muted-foreground">
            {crewContext.openCount} open team slot{crewContext.openCount === 1 ? '' : 's'} on this job. Workers already assigned are not replaced.
          </p>
        </div>
        <div className={cn('flex gap-2', isCompact ? 'w-full flex-col sm:w-auto sm:flex-row sm:items-center' : 'w-full flex-col md:w-auto md:min-w-[280px]')}>
          <Label className="sr-only">Ready team</Label>
          <Select value={selectedCrewId || 'none'} onValueChange={(value) => setSelectedCrewId(value === 'none' ? '' : value)}>
            <SelectTrigger className={cn(isCompact ? 'h-8 text-xs' : undefined)}>
              <SelectValue placeholder={readyCrewsQuery.isLoading ? 'Loading teams...' : 'Select staffed team'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Select team</SelectItem>
              {(readyCrewsQuery.data ?? []).map((crew) => (
                <SelectItem key={String(crew.id)} value={String(crew.id)}>{crew.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size={isCompact ? 'sm' : 'default'}
            className={cn(isCompact ? 'h-8 shrink-0' : undefined)}
            disabled={disabled || !selectedCrewId || assignMutation.isPending || readyCrewsQuery.isLoading}
            onClick={() => assignMutation.mutate()}
          >
            {assignMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Users className="mr-2 h-4 w-4" />}
            Assign team
          </Button>
        </div>
      </div>
      {!readyCrewsQuery.isLoading && (readyCrewsQuery.data ?? []).length === 0 ? (
        <p className="mt-2 text-xs text-amber-800">No ready teams for this template. Staff one under Services → Teams.</p>
      ) : null}
    </div>
  )
}

export default JobAssignCrewControls
